import React, { useState, useEffect } from 'react'
import LoginPage from './components/login/login-page'
import { AuthProvider } from './components/auth/auth-context'
import { MessageProvider } from './components/message/message-context'
import BaseAccountLayout from './components/layout/base-account-layout'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import AgreementPage from './components/agreements/agreements-page'
import StatisticsPage from './components/statistic/statistics-page'
import UsersPage from './components/user-management/user-page'
import BillingInfo from './components/billing-info/billing-info'
import Dashboard from './components/dashboard/dashboard'
import PayData from './components/pay-data/pay-data'
import DashboardHeader from './components/dashboard/dashboard-header'
import GuaranteedAmountPage from './components/guaranteed-amount/guaranteed-amount-page'
import { getLocalStorageToken, setLocalStorageToken } from './components/auth/tokenStorage'

const App: React.FC = () => {
  const getMessageColors = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green text-white'
      case 'warn':
        return 'text-black'
      case 'error':
        return 'bg-fastigoRed text-white'
      default:
        return 'text-darkBlue bg-white'
    }
  }
  const localStorageToken = getLocalStorageToken()
  const [token, setToken] = useState(localStorageToken)
  const [tokenData, setTokenData] = useState({
    accounts: [],
    iat: 0,
    roles: [],
    sub: '',
  })
  const [message, setMessage] = useState({
    value: '',
    type: '',
    status: '',
    title: '',
  })
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (message.value !== '') {
      setErrorMessage('')
      setTimeout(() => setMessage({ value: '', type: '', status: '', title: '' }), 10000)
    }
  }, [message])

  useEffect(() => {
    if (errorMessage !== '') {
      setMessage({ value: '', type: '', status: '', title: '' })
      setTimeout(() => setErrorMessage(''), 10000)
    }
  }, [errorMessage])

  // TODO: Why is this here?

  // useEffect(() => {
  //   window.addEventListener('beforeunload', (event) => {
  //     console.log('LEAVE EVENT', event)

  //     // Cancel the event as stated by the standard.
  //     event.preventDefault()
  //     // Older browsers supported custom message
  //     console.log('asking for leave')
  //   })
  // }, [])

  const router = createBrowserRouter([
    {
      path: '/',
      element: <BaseAccountLayout />,
      children: [
        {
          index: true,
          element: (
            <div className="flex flex-col gap-12 ">
              <DashboardHeader />
              <Dashboard />
            </div>
          ),
        },
        {
          path: '/anvandare',
          element: (
            <div className="flex flex-col gap-12 ">
              <DashboardHeader />
              <UsersPage />
            </div>
          ),
        },
        {
          path: '/antal-anstallda',
          element: <AgreementPage />,
        },
        {
          path: '/faktureringsinformation',
          element: (
            <div className="flex flex-col gap-12 ">
              <DashboardHeader />
              <BillingInfo fromForm={false} />
            </div>
          ),
        },
        {
          path: '/lonestatistik',
          element: <StatisticsPage />,
        },
        {
          path: '/garanibelopp',
          element: (
            <div className="flex flex-col gap-12 ">
              <DashboardHeader />
              <GuaranteedAmountPage />
            </div>
          ),
        },
        {
          path: '/arslonesummor',
          element: <PayData />,
        },
      ],
    },
  ])

  return (
    <MessageProvider
      value={{
        message,
        setMessage,
        errorMessage,
        setErrorMessage,
      }}
    >
      <AuthProvider
        value={{
          token,
          setToken: (value: string) => {
            setLocalStorageToken(value, () => setToken(value))
          },
          tokenData,
          setTokenData,
        }}
      >
        {token ? <RouterProvider router={router} /> : <LoginPage />}
        {message && message.value && !message.type ? (
          <div
            className="fixed flex w-full top-0 right-0 bg-black shadow border-lightGray text-fastigoWhite px-8 py-2 justify-between z-50"
            role="alert"
          >
            <div className="flex">
              <FontAwesomeIcon icon={faInfoCircle} size="lg" color="#fff" className="mr-4" />
              <div>
                <p className="text-sm font-semibold">{message.value}</p>
              </div>
            </div>
          </div>
        ) : null}
        {message && message.value && message.type === 'modal' ? (
          <div className="fixed top-0 left-0 bg-lightGray w-full h-full flex justify-center items-center">
            <div
              className={`message-modal w-4/5 h-4/5 sm:w-128 sm:h-96 flex justify-center items-center shadow-md ${getMessageColors(
                message.status,
              )}`}
              style={message.status === 'warn' ? { backgroundColor: '#fceb8d' } : {}}
            >
              <div className="flex justify-between flex-col px-12">
                {message.title ? <h1 className="text-3xl font-bold">{message.title}</h1> : null}
                <p className="text-lg">{message.value}</p>
                <button
                  className="text-left mt-24 underline font-bold uppercase text-sm"
                  onClick={() => {
                    setMessage({ value: '', type: '', status: '', title: '' })
                  }}
                >
                  Stäng
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {errorMessage ? (
          <div
            className="fixed flex w-full top-0 right-0 bg-red shadow border-lightGray text-fastigoWhite px-8 py-2 justify-between z-50"
            role="alert"
          >
            <div className="flex">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                size="lg"
                color="#fff"
                className="mr-4"
              />
              <div>
                <p className="text-sm font-semibold">{errorMessage}</p>
              </div>
            </div>
          </div>
        ) : null}
      </AuthProvider>
    </MessageProvider>
  )
}

export default App
